import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import type DocumentSettingsFormData from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/FormData/DocumentSettingsFormData';
import SectionCard from '@Guidelines/Component/SectionCard';
import type DocumentTypePresetView from '@UI/Store/OrganizationWorkflows/Views/DocumentTypePresetView';
import * as React from 'react';
import { Button, Tag, TextArea } from '@verclam/ui-kit';

type Props = {
	readonly settings: DocumentSettingsFormData;
	readonly preset: DocumentTypePresetView | null;
	readonly onChange: React.Dispatch<React.SetStateAction<DocumentSettingsFormData>>;
};

/**
 * The two prompts of the document, shown as the preset of its type until one is edited; reverting
 * sends both back to the catalogue default.
 */
const PromptsSection: React.FC<Props> = ({ settings, preset, onChange }) => {
	const handleSystemPromptChange = React.useCallback(
		(value: string) => {
			onChange((current) => current.setSystemPrompt(value));
		},
		[onChange],
	);

	const handleContextPromptChange = React.useCallback(
		(value: string) => {
			onChange((current) => current.setContextPrompt(value));
		},
		[onChange],
	);

	const handleRevertClick = React.useCallback(() => {
		onChange((current) => current.setSystemPrompt(null).setContextPrompt(null));
	}, [onChange]);

	const isPersonalized = settings.hasPersonalizedPrompts;

	return (
		<SectionCard
			title="Consignes"
			aside={
				<Tag
					value={isPersonalized ? 'Personnalisée' : 'Texte par défaut'}
					severity={isPersonalized ? 'warning' : 'secondary'}
					rounded
				/>
			}
		>
			<TextArea
				name="systemPrompt"
				label="Consignes"
				rows={3}
				value={settings.systemPrompt ?? preset?.systemPrompt ?? ''}
				onChange={handleSystemPromptChange}
			/>
			<TextArea
				name="contextPrompt"
				label="Demande"
				rows={3}
				value={settings.contextPrompt ?? preset?.contextPrompt ?? ''}
				onChange={handleContextPromptChange}
			/>
			<ConditionalLoader data={isPersonalized}>
				<div>
					<Button
						type="button"
						size="sm"
						severity="secondary"
						label="Revenir au texte par défaut"
						onClick={handleRevertClick}
					/>
				</div>
			</ConditionalLoader>
		</SectionCard>
	);
};

export default PromptsSection;
